import { useEffect, useState } from "react";
import { ILoginInfo } from "../models/ILoginInfo";
import { decrypt } from "./Util";

/**
 * Çoklu restoran: bir hesap birden çok restorana yönetici olarak bağlı olabilir.
 *
 * "Aktif restoran" SEKME başınadır (sessionStorage): biri bir sekmede A'ya, diğerinde B'ye
 * bakabilir. Seçim yoksa girişteki varsayılan `restourantId` kullanılır. Restoran bazlı
 * çalışan bütün sayfalar id'yi buradan okur ki Dashboard'da yapılan seçim her yerde geçerli olsun.
 */

const ACTIVE_KEY = "activeRestaurantId";
const KNOWN_KEY = "knownRestaurants";
const CHANGE_EVENT = "active-restaurant-changed";
/** Yeni sekmede belirli bir restoranı açmak için: /dashboard?restaurant=<id> */
export const RESTAURANT_URL_PARAM = "restaurant";

export interface ILinkedRestaurant {
  id: string;
  name: string;
}

function storedUser(): ILoginInfo | null {
  try {
    const raw = localStorage.getItem("user");
    return raw ? (JSON.parse(decrypt(raw)) as ILoginInfo) : null;
  } catch {
    return null;
  }
}

function safeSession<T>(fn: () => T, fallback: T): T {
  try { return fn(); } catch { return fallback; }
}

/** Girişte gelen liste + my-restaurants'tan öğrenilen liste (eski oturumlarda giriş listesi yok). */
export function getLinkedRestaurants(): ILinkedRestaurant[] {
  const fromLogin = storedUser()?.restaurants ?? [];
  const fromApi = safeSession<ILinkedRestaurant[]>(
    () => JSON.parse(sessionStorage.getItem(KNOWN_KEY) ?? "[]"),
    []
  );
  const byId = new Map<string, ILinkedRestaurant>();
  [...fromLogin, ...fromApi].forEach((r) => { if (r?.id) byId.set(r.id.toLowerCase(), r); });
  return Array.from(byId.values());
}

/** my-restaurants yanıtı geldiğinde çağrılır; doğrulama ve "çok restoranlı mı" bilgisi için. */
export function rememberLinkedRestaurants(list: ILinkedRestaurant[]) {
  safeSession(() => sessionStorage.setItem(
    KNOWN_KEY, JSON.stringify(list.map(({ id, name }) => ({ id, name })))
  ), undefined);
}

export function hasMultipleRestaurants(): boolean {
  return getLinkedRestaurants().length > 1;
}

function isLinked(id: string): boolean {
  const linked = getLinkedRestaurants();
  // Liste henüz bilinmiyorsa (eski oturum, yeni açılmış sekme) seçim kabul edilir; yetkisiz bir
  // id'yi sunucunun erişim kontrolü reddeder. Liste öğrenilince (my-restaurants) doğrulama başlar.
  if (linked.length === 0) return true;
  return linked.some((r) => r.id.toLowerCase() === id.toLowerCase());
}

/** Bu sekmenin restoranı: geçerli bir seçim varsa o, yoksa girişteki varsayılan. */
export function getActiveRestaurantId(): string {
  const chosen = safeSession(() => sessionStorage.getItem(ACTIVE_KEY), null);
  if (chosen && isLinked(chosen)) return chosen;
  return storedUser()?.restourantId ?? "";
}

export function setActiveRestaurantId(id: string) {
  if (!id || !isLinked(id)) return;
  safeSession(() => sessionStorage.setItem(ACTIVE_KEY, id), undefined);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: id }));
}

/**
 * Adres çubuğunda ?restaurant=<id> varsa bu sekmenin aktif restoranı yapar ve parametreyi
 * adresten siler (yenilemede tekrar uygulanmasın, paylaşılan linke yapışmasın).
 */
export function applyRestaurantFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const id = params.get(RESTAURANT_URL_PARAM);
  if (!id) return;
  // Render sırasında çağrılabildiği için olay yayınlanmaz; okuyanlar ilk kurulumda zaten güncel değeri alır.
  if (isLinked(id)) safeSession(() => sessionStorage.setItem(ACTIVE_KEY, id), undefined);
  params.delete(RESTAURANT_URL_PARAM);
  const query = params.toString();
  window.history.replaceState(window.history.state, "", window.location.pathname + (query ? `?${query}` : "") + window.location.hash);
}

/** Belirli bir restoranın dashboard'unun adresi (yeni sekmede açmak için). */
export function dashboardUrlFor(restaurantId: string): string {
  return `${window.location.origin}${process.env.PUBLIC_URL ?? ""}/dashboard?${RESTAURANT_URL_PARAM}=${encodeURIComponent(restaurantId)}`;
}

/**
 * "Benim restoranım" uçları için sorgu eki. Tek restoranlı hesapta parametre gönderilmez
 * (eski davranış); çok restoranlıda gönderilmezse sunucu 400 RestaurantSelectionRequired döner.
 */
export function restaurantQuery(prefix: "?" | "&" = "?"): string {
  if (!hasMultipleRestaurants()) return "";
  const id = getActiveRestaurantId();
  return id ? `${prefix}restaurantId=${encodeURIComponent(id)}` : "";
}

/** Aktif restoranı izler; aynı sekmede seçim değişince yeniden render eder. */
export function useActiveRestaurantId(): string {
  const [id, setId] = useState(getActiveRestaurantId);
  useEffect(() => {
    const onChange = () => setId(getActiveRestaurantId());
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);
  return id;
}
